import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth';
import { qk } from '@/lib/queryKeys';
import { api, apiHelpers } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { formatTeamRole } from '@/lib/utils';
import { useTheme } from '@/hooks/useTheme';
import { User, Lock, Bell, Palette, Loader2, Check, Eye, EyeOff, KeyRound } from 'lucide-react';

export default function Settings() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'tools', label: 'Connected tools', icon: KeyRound },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account settings and preferences</p>
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        {/* Sidebar */}
        <div className="flex-shrink-0 md:w-64">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && <ProfileSettings user={user} />}
          {activeTab === 'security' && <SecuritySettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'appearance' && <AppearanceSettings />}
          {activeTab === 'tools' && <ConnectedToolsSettings />}
        </div>
      </div>
    </div>
  );
}

function ProfileSettings({ user }: { user: any }) {
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const { refreshUser } = useAuthStore();

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const updates: Record<string, string> = {};
      if (firstName !== user?.firstName) updates.firstName = firstName;
      if (lastName !== user?.lastName) updates.lastName = lastName;
      if (email !== user?.email) updates.email = email;

      if (Object.keys(updates).length === 0) {
        setIsSaving(false);
        return;
      }

      await apiHelpers.updateUser(user?.id, updates);
      // Refresh user data in auth store so sidebar/header reflect changes
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || 'Failed to update profile';
      setError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border p-6">
      <h2 className="mb-4 text-lg font-semibold">Profile Information</h2>
      <form onSubmit={handleSave} className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Role</label>
          <input
            type="text"
            value={
              user?.role === 'SUPER_ADMIN'
                ? 'Super Admin'
                : user?.teamRole
                  ? formatTeamRole(user.teamRole)
                  : user?.role?.replace('_', ' ')
            }
            disabled
            className="bg-muted text-muted-foreground w-full rounded-lg border px-3 py-2"
          />
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 font-medium disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <>
              <Check className="h-4 w-4" />
              Saved!
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </form>
    </div>
  );
}

function SecuritySettings() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsSaving(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      // The API returns `error` as an object ({ code, message }); rendering an
      // object as a React child crashes the page (white screen). Always extract
      // the string message.
      setError(apiErrorMessage(err, 'Failed to change password'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border p-6">
        <h2 className="mb-4 text-lg font-semibold">Change Password</h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Current Password</label>
            <div className="relative">
              <input
                type={showPasswords ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 pr-10 focus:outline-none focus:ring-2"
              />
              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">New Password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Confirm New Password</label>
            <input
              type={showPasswords ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
            />
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2 rounded-lg px-4 py-2 font-medium disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Password'}
          </button>
        </form>
      </div>

      {/* An "Enable 2FA" button lived here with no onClick. There is no 2FA
          enrolment flow behind it, and a security control that appears to work
          is worse than none: a user can believe their account is protected when
          it isn't. Removed until enrolment exists. */}
    </div>
  );
}

function NotificationSettings() {
  // These four toggles were local useState that was never loaded from or saved
  // to the server: every change evaporated on navigation. Until there is a
  // preferences endpoint they are rendered read-only and clearly labelled, so
  // the page stops pretending to accept input it throws away.
  const preferences = [
    {
      label: 'Email Notifications',
      description: 'Receive notifications via email',
      enabled: true,
    },
    {
      label: 'New Contact Submissions',
      description: 'Get notified when someone submits a contact form',
      enabled: true,
    },
    {
      label: 'New Newsletter Subscribers',
      description: 'Get notified when someone subscribes',
      enabled: true,
    },
    {
      label: 'Weekly Digest',
      description: 'Receive a weekly summary of activity',
      enabled: false,
    },
  ];

  return (
    <div className="bg-card rounded-xl border p-6">
      <h2 className="text-lg font-semibold">Notification Preferences</h2>
      <p className="text-muted-foreground mt-1 text-sm">
        Not configurable yet. These are the current defaults for your account — per-user control is
        coming.
      </p>
      <div className="mt-4 space-y-4">
        {preferences.map((pref) => (
          <div key={pref.label} className="flex items-center justify-between opacity-60">
            <div>
              <p className="font-medium">{pref.label}</p>
              <p className="text-muted-foreground text-sm">{pref.description}</p>
            </div>
            <input
              type="checkbox"
              checked={pref.enabled}
              disabled
              readOnly
              aria-label={`${pref.label} (not configurable yet)`}
              className="text-primary h-5 w-5 rounded border-gray-300"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function AppearanceSettings() {
  // Reads and writes the same store the header toggle uses, so the two controls
  // can no longer disagree, and the choice survives a reload.
  const { theme, setTheme } = useTheme();

  return (
    <div className="bg-card rounded-xl border p-6">
      <h2 className="mb-4 text-lg font-semibold">Appearance</h2>
      <div className="space-y-4">
        <div>
          <label className="mb-3 block text-sm font-medium">Theme</label>
          <div className="grid grid-cols-3 gap-3">
            {(['light', 'dark', 'system'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={`rounded-lg border p-4 text-center capitalize transition-colors ${
                  theme === t ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                }`}
              >
                <div
                  className={`mx-auto mb-2 h-8 w-8 rounded-full ${
                    t === 'light'
                      ? 'bg-yellow-400'
                      : t === 'dark'
                        ? 'bg-slate-800'
                        : 'bg-gradient-to-r from-yellow-400 to-slate-800'
                  }`}
                />
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface CliToken {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  active: boolean;
}

/** Tools signed in to this account with the device flow (/device). */
function ConnectedToolsSettings() {
  const queryClient = useQueryClient();
  const tokens = useQuery({
    queryKey: qk.cliTokens(),
    queryFn: async () => (await api.get('/auth/cli/tokens')).data.data as CliToken[],
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => api.delete(`/auth/cli/tokens/${id}`),
    onSuccess: () => {
      toast.success('Access revoked');
      queryClient.invalidateQueries({ queryKey: qk.cliTokens() });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Could not revoke access')),
  });
  const active = (tokens.data ?? []).filter((t) => t.active);
  const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="bg-card rounded-xl border p-6">
      <h2 className="mb-1 text-lg font-semibold">Connected tools</h2>
      <p className="text-muted-foreground mb-6 text-sm">
        Apps you allowed to work on your website from your computer, such as the Tyashin plugin for
        Claude. They cannot buy anything, change billing or manage your team.
      </p>
      {tokens.isPending ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : tokens.isError ? (
        <p className="text-sm text-red-600">{apiErrorMessage(tokens.error, 'Could not load')}</p>
      ) : active.length === 0 ? (
        <p className="text-muted-foreground text-sm">No tools are connected.</p>
      ) : (
        <ul className="divide-y">
          {active.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-4 py-3">
              <div className="min-w-0">
                <div className="truncate font-medium">{t.name}</div>
                <div className="text-muted-foreground text-xs">
                  <span className="font-mono">{t.prefix}…</span> · connected {fmt(t.createdAt)} ·
                  last used {fmt(t.lastUsedAt)} · expires {fmt(t.expiresAt)}
                </div>
              </div>
              <button
                onClick={() => revoke.mutate(t.id)}
                disabled={revoke.isPending}
                className="shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
